import { DropdownOption } from '@/components/actions/types';
import { isWebApplication } from '@/hooks/useAssets';
import { SCAN_TYPE_CONFIGS } from '@/sections/asset/constants';
import { ActionOptionBuilderParams } from '@/sections/asset/types';
import { Asset, AssetStatus } from '@/types';

/**
 * Builds scan dropdown options with conditional enabling
 * Options are disabled based on asset types, status, and user permissions
 *
 * @param params - Parameters for building scan options
 * @returns Array of dropdown options for scan actions
 */
export const getAssetScanOptions = ({
  assets,
  handlers,
  selectUserAgent,
  isPraetorianUser: _isPraetorianUser,
}: ActionOptionBuilderParams): DropdownOption[] => {
  // Check if we have actual WebApplication assets
  const hasWebApplications = assets.some(asset => isWebApplication(asset));

  // Check if assets have HTTP/HTTPS indicators (attributes, ports, or class)
  const hasWebServices = assets.some(asset => {
    // Direct WebApplication check
    if (isWebApplication(asset)) {
      return true;
    }

    // Check asset class field for web-related classes (available without attributes)
    if (asset.class) {
      const webClasses = ['http', 'https', 'webapp', 'webapplication', 'web'];
      if (webClasses.some(cls => asset.class?.toLowerCase().includes(cls))) {
        return true;
      }
    }

    // Check ports array for common HTTP/HTTPS ports (80, 443, 8080, 8443, etc.)
    if (asset.ports && asset.ports.length > 0) {
      const webPorts = [80, 443, 8080, 8443, 8000, 8888, 3000, 5000];
      if (asset.ports.some(port => webPorts.includes(Number(port.port)))) {
        return true;
      }
    }

    // Check if asset has HTTP/HTTPS attributes (when attributes are loaded, e.g., in drawer)
    if (asset.attributes && asset.attributes.length > 0) {
      return asset.attributes.some(
        attr => attr.name === 'http' || attr.name === 'https'
      );
    }

    return false;
  });

  // Show web scanning options if we have WebApplications or confirmed web services
  const showWebScanOptions = hasWebApplications || hasWebServices;

  // Show network scanning options if we don't have ONLY WebApplications
  const showNetworkScanOptions = !hasWebApplications;

  // Disable scanning for Frozen, Pending, or Deleted assets
  const hasNonScannableAssets = assets.some(
    asset =>
      asset.status === AssetStatus.Frozen ||
      asset.status === AssetStatus.Pending ||
      asset.status === AssetStatus.Deleted
  );

  // AMI scan only applies to AWS::EC2::Image resources (caligula-ami match condition)
  const showAmiScanOptions = assets.every(
    asset =>
      asset.key?.startsWith('#awsresource#') &&
      (asset as Asset & { resourceType?: string }).resourceType ===
        'AWS::EC2::Image'
  );

  const options: DropdownOption[] = [
    {
      label: SCAN_TYPE_CONFIGS.comprehensive.label,
      icon: <SCAN_TYPE_CONFIGS.comprehensive.icon className="size-4" />,
      onClick: () => handlers.handleScan(assets, 'comprehensive'),
      disabled: hasNonScannableAssets,
    },
    {
      label: SCAN_TYPE_CONFIGS.network.label,
      icon: <SCAN_TYPE_CONFIGS.network.icon className="size-4" />,
      onClick: () => handlers.handleScan(assets, 'network'),
      disabled: !showNetworkScanOptions || hasNonScannableAssets,
    },
    {
      label: SCAN_TYPE_CONFIGS.port.label,
      icon: <SCAN_TYPE_CONFIGS.port.icon className="size-4" />,
      onClick: () => handlers.handleScan(assets, 'port'),
      disabled: !showNetworkScanOptions || hasNonScannableAssets,
    },
    {
      label: SCAN_TYPE_CONFIGS.crawl.label,
      icon: <SCAN_TYPE_CONFIGS.crawl.icon className="size-4" />,
      onClick: () => {
        // Web crawl requires user agent selection modal
        selectUserAgent.onAssetKeysChange(assets.map(asset => asset.key));
      },
      disabled: !showWebScanOptions || hasNonScannableAssets,
    },
    {
      label: SCAN_TYPE_CONFIGS.webscan.label,
      icon: <SCAN_TYPE_CONFIGS.webscan.icon className="size-4" />,
      onClick: () => handlers.handleScan(assets, 'webscan'),
      disabled: !showWebScanOptions || hasNonScannableAssets,
    },
  ];

  // AMI scan only applies to EC2 images — omit entirely rather than showing it
  // permanently disabled, which clutters the menu for every other asset type.
  if (showAmiScanOptions) {
    options.push({
      label: SCAN_TYPE_CONFIGS.ami.label,
      icon: <SCAN_TYPE_CONFIGS.ami.icon className="size-4" />,
      onClick: () => handlers.handleScan(assets, 'ami'),
      disabled: hasNonScannableAssets,
    });
  }

  return options;
};
